import {
    InfringementRaisedCondition,
    anyTypeInfringementKindKey,
    getInfringementRaisedTriggerType,
    infringementRaisedTriggerKey,
    specificCodeInfringementKindKey
} from 'pages/employee-reviews/shared/triggers/InfringementRaisedCondition';

// The server's wording, so a message reads the same whether the editor or the API catches it.
export const infringementCountMessage = "A trigger's infringement count must be between 1 and 1000.";
export const infringementTypeRequiredMessage = "A trigger for a specific infringement type needs the infringement type.";
export const infringementTypeInactiveMessage = "The infringement type on this trigger is no longer active. Choose another type.";

const minimumInfringementCount = 1;
const maximumInfringementCount = 1000;

// Only a specific-type trigger names an infringement type; any other kind is held and sent
// without one, so a type picked and then abandoned never reaches the API or a comparison.
function normaliseInfringementRaisedCondition(condition) {
    const specificCode = condition?.infringementKind === specificCodeInfringementKindKey;

    return {
        conditionCount: condition?.conditionCount ?? null,
        infringementKind: condition?.infringementKind ?? null,
        infringementTypeId: specificCode ? (condition.infringementTypeId ?? null) : null
    };
}

function describeInfringementKind(condition, reviewOptions) {
    if (condition.infringementKind === specificCodeInfringementKindKey) {
        const infringementType = (reviewOptions?.infringementTypes ?? []).find((item) => item.id === condition.infringementTypeId);

        if (infringementType == null) {
            return "of a specific type";
        }

        return `of type ${infringementType.code}`;
    }

    const kind = (getInfringementRaisedTriggerType(reviewOptions)?.infringementKinds ?? []).find((item) => item.key === condition.infringementKind);

    return `of ${kind?.name ?? "any type"}`;
}

const infringementRaisedTriggerType = {
    ConditionEditor: InfringementRaisedCondition,

    buildDefaultCondition() {
        return {
            conditionCount: 1,
            infringementKind: anyTypeInfringementKindKey,
            infringementTypeId: null
        };
    },

    conditionFromApi(apiTrigger) {
        return normaliseInfringementRaisedCondition(apiTrigger);
    },

    conditionToApi(condition) {
        return normaliseInfringementRaisedCondition(condition);
    },

    validateCondition(condition, reviewOptions) {
        const errors = {};
        const count = condition?.conditionCount;

        if (!Number.isInteger(count) || count < minimumInfringementCount || count > maximumInfringementCount) {
            errors.conditionCount = infringementCountMessage;
        }

        if (condition?.infringementKind === specificCodeInfringementKindKey) {
            const activeTypes = reviewOptions?.infringementTypes;

            if (condition.infringementTypeId == null) {
                errors.infringementTypeId = infringementTypeRequiredMessage;
            } else if (activeTypes != null && !activeTypes.some((infringementType) => infringementType.id === condition.infringementTypeId)) {
                // The API only accepts active infringement types, so a type made inactive since
                // the trigger was saved has to be replaced before the review type can be saved.
                errors.infringementTypeId = infringementTypeInactiveMessage;
            }
        }

        return errors;
    },

    isSameCondition(first, second) {
        const a = normaliseInfringementRaisedCondition(first);
        const b = normaliseInfringementRaisedCondition(second);

        return a.conditionCount === b.conditionCount &&
            a.infringementKind === b.infringementKind &&
            a.infringementTypeId === b.infringementTypeId;
    },

    // Only a trigger counting the same infringements - the same kind and, for a specific type, the
    // same infringement type - is certain to be met first, and only when its count is no higher.
    covers(coveringCondition, condition) {
        const covering = normaliseInfringementRaisedCondition(coveringCondition);
        const covered = normaliseInfringementRaisedCondition(condition);

        return covering.infringementKind === covered.infringementKind &&
            covering.infringementTypeId === covered.infringementTypeId &&
            Number.isInteger(covering.conditionCount) &&
            Number.isInteger(covered.conditionCount) &&
            covering.conditionCount <= covered.conditionCount;
    },

    describe(condition, reviewOptions) {
        const normalised = normaliseInfringementRaisedCondition(condition);
        const singular = normalised.conditionCount === 1;
        const noun = singular ? "infringement" : "infringements";
        const verb = singular ? "is" : "are";

        return `When ${normalised.conditionCount} ${noun} ${describeInfringementKind(normalised, reviewOptions)} ${verb} raised since the last review`;
    }
};

// Each trigger type the editor can offer, keyed by the API's trigger key. Every entry supplies
// ConditionEditor, buildDefaultCondition, conditionFromApi, conditionToApi (only the trigger's
// own request properties), validateCondition (messages keyed by condition field, given the review
// options), isSameCondition, covers (whether the first condition is always met no later than the
// second, which makes the second redundant beside it) and describe, so the triggers table, the
// save and the summary stay free of any one trigger type's fields.
export const reviewTriggerTypes = {
    [infringementRaisedTriggerKey]: infringementRaisedTriggerType
};

export function getReviewTriggerType(triggerKey) {
    return reviewTriggerTypes[triggerKey] ?? null;
}
